// useCloudFileShare — S8 internet file sharing (Supabase-only).
//
// Beam moves files LAN-only over WebRTC. When the active data provider
// advertises `capabilities.internetFileShare` (Supabase: true, Firebase:
// false), this hook exposes an additional "share over the internet" path:
// upload the file to cloud Storage, then post a file message whose attachment
// carries the download URL. Every recipient — on any network, now or on
// reload — renders that attachment as a download link (see FileCard /
// Bubble's FileAttachment).
//
// This is deliberately a thin hook over the frozen data layer
// (src/lib/data/**): all the storage + message-write logic lives there. The
// hook only wires identity, the capability gate, and user-facing toast/error
// handling so the call sites (DesktopBeam, future mobile share sheet) stay
// declarative.
//
// Returned shape:
//   {
//     enabled: boolean,            // capabilities.internetFileShare
//     sharing: boolean,            // an upload+send is in flight (any file)
//     shareFiles(files): Promise,  // upload + post each file, sequentially
//   }
//
// Progress note: supabase-js Storage upload has no native progress event, so
// `sharing` is an indeterminate boolean for v1 (an indeterminate spinner is
// acceptable per the S8 spec). A real progress bar would require an XHR-based
// upload in the provider — out of scope here and the provider is frozen.

import { useCallback, useRef, useState } from 'react';
import {
  capabilities,
  uploadFile,
  sendFileMessage,
} from '../lib/data';
import { useIdentity } from './useIdentity';
import useToast from '../components/toast/useToast';

// Whether the active provider supports internet file share. The provider is
// fixed at build time (VITE_DATA_PROVIDER), so the value is effectively
// constant within a session; we read it from the live `capabilities` map
// (rather than snapshotting at module load) so the gate stays simple and
// testable.
const internetFileShareEnabled = () =>
  !!(capabilities && capabilities.internetFileShare);

export function useCloudFileShare() {
  const { user, handle, partyCode, avatarSeed } = useIdentity();
  const { showToast } = useToast();

  const enabled = internetFileShareEnabled();
  const [sharing, setSharing] = useState(false);
  // Guard against overlapping share batches (e.g. a double-click). We still
  // allow the in-flight batch to finish; a second invocation is a no-op.
  const inFlightRef = useRef(false);

  const shareFiles = useCallback(
    async (files) => {
      if (!internetFileShareEnabled()) return; // capability gate — never reached when UI is hidden
      const list = Array.isArray(files)
        ? files
        : files
          ? Array.from(files)
          : [];
      if (list.length === 0) return;
      if (!partyCode) {
        showToast('Join a party to share over the internet');
        return;
      }
      if (inFlightRef.current) return;

      inFlightRef.current = true;
      setSharing(true);
      let sent = 0;
      try {
        // Sequential, not parallel: keeps memory + the storage request rate
        // bounded, and a single failed upload doesn't abort the rest. Each
        // file toasts independently on failure, mirroring the LAN drop loop
        // in DesktopBeam.
        for (const file of list) {
          if (!file) continue;
          try {
            const ref = await uploadFile({ code: partyCode, file, senderId: user });
            await sendFileMessage({
              code: partyCode,
              name: file.name,
              size: file.size,
              mime: file.type,
              via: 'cloud',
              senderId: user,
              senderName: handle,
              avatarSeed,
              attachment: ref,
            });
            sent += 1;
          } catch (err) {
            // Surface a humane toast and keep going. We don't crash the UI;
            // the partial batch (files that did upload) is already posted.
            const label = (file && file.name) || 'file';
            showToast(`Couldn't share ${label} over the internet`);
            // Aid debugging without leaking the error object to the UI.
            if (typeof console !== 'undefined' && console.error) {
              console.error('[cloud-share] upload/send failed', err);
            }
          }
        }
        // Success feedback: the resulting file message lands in the chat
        // (often behind the Beam overlay), so without this toast a successful
        // share looks like "nothing happened". Confirm explicitly.
        if (sent > 0) {
          showToast(
            sent === 1
              ? 'Shared over the internet — sent to the room'
              : `Shared ${sent} files over the internet`,
          );
        }
      } finally {
        inFlightRef.current = false;
        setSharing(false);
      }
    },
    [partyCode, user, handle, avatarSeed, showToast],
  );

  return { enabled, sharing, shareFiles };
}

export default useCloudFileShare;
